import React from "react";

import { cn } from "@/lib/utils";
import { WidgetType } from "@/types/widget";

interface ChartLoadingWrapperProps {
  isLoading: boolean;
  chartType: WidgetType;
  children?: React.ReactNode;
}

const Bone = ({
  className,
  style
}: {
  className?: string;
  style?: React.CSSProperties;
}) => <div className={cn("rounded bg-whisper", className)} style={style} />;

/** Stacked short lines mimicking a chart's y-axis tick labels. */
const AxisLabels = ({ count }: { count: number }) => (
  <div className="flex flex-col justify-between py-2">
    {Array.from({ length: count }, (_, index) => (
      <Bone key={index + 1} className="h-2 w-6" />
    ))}
  </div>
);

const DonutSkeleton = () => (
  <div className="flex h-[450px] flex-col gap-6">
    <div className="flex flex-1 items-center justify-between gap-4">
      <div className="flex flex-col gap-6">
        <Bone className="h-2 w-12" />
        <Bone className="h-2 w-10" />
        <Bone className="h-2 w-12" />
      </div>
      <div className="relative size-[220px] shrink-0">
        <div className="size-full rounded-full bg-whisper" />
        <div className="absolute left-1/2 top-1/2 size-[100px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
      </div>
      <div className="flex flex-col gap-6">
        <Bone className="h-2 w-12" />
        <Bone className="h-2 w-10" />
        <Bone className="h-2 w-12" />
      </div>
    </div>
    <div className="flex flex-wrap items-center justify-center gap-3">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index + 1} className="flex items-center gap-1.5">
          <Bone className="size-2 rounded-full" />
          <Bone className="h-2 w-8" />
        </div>
      ))}
    </div>
  </div>
);

const VERTICAL_BAR_HEIGHTS = [
  70, 25, 20, 35, 45, 30, 22, 40, 18, 30, 55, 28, 42, 80, 60
];

const VerticalBarSkeleton = () => (
  <div className="flex h-[450px] gap-2">
    <AxisLabels count={5} />
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 items-end justify-between gap-1.5">
        {VERTICAL_BAR_HEIGHTS.map((height, index) => (
          <Bone
            key={index + 1}
            className="w-full rounded-b-none"
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between gap-1.5">
        {VERTICAL_BAR_HEIGHTS.map((_, index) => (
          <Bone key={index + 1} className="h-2 w-full -rotate-45" />
        ))}
      </div>
    </div>
  </div>
);

const LineSkeleton = () => (
  <div className="flex h-[450px] gap-2">
    <AxisLabels count={6} />
    <div className="flex flex-1 flex-col">
      <div className="relative flex-1">
        <svg
          className="size-full stroke-whisper text-whisper"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polyline
            points="0,78 12,80 24,79 36,77 48,72 60,45 72,28 84,22 100,15"
            fill="none"
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      <div className="mt-2 flex justify-between gap-1.5">
        {Array.from({ length: 10 }, (_, index) => (
          <Bone key={index + 1} className="h-2 w-full" />
        ))}
      </div>
    </div>
  </div>
);

const HORIZONTAL_BAR_WIDTHS = [95, 62, 40, 45, 32, 55, 38, 28, 22, 18, 12];

const HorizontalBarSkeleton = () => (
  <div className="flex h-[450px] gap-3">
    <div className="flex flex-col justify-between py-1">
      {HORIZONTAL_BAR_WIDTHS.map((_, index) => (
        <Bone key={index + 1} className="h-2 w-6" />
      ))}
    </div>
    <div className="relative flex flex-1 flex-col justify-between py-1">
      <div className="absolute inset-0 flex justify-between">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index + 1} className="w-px bg-white-smoke" />
        ))}
      </div>
      {HORIZONTAL_BAR_WIDTHS.map((width, index) => (
        <Bone
          key={index + 1}
          className="relative h-4 rounded-l-none"
          style={{ width: `${width}%` }}
        />
      ))}
    </div>
  </div>
);

export const BigNumberSkeleton = () => (
  <div className="flex h-[90px] gap-3 items-center justify-start border border-gainsboro rounded-xl p-2 w-full">
    <Bone className="h-20 w-2" />
    <div>
      <Bone className="h-6 w-20 mb-2" />
      <Bone className="h-12 w-32" />
    </div>
  </div>
);

const SKELETON_BY_CHART_TYPE: Record<WidgetType, React.ReactNode> = {
  [WidgetType.DONUT]: <DonutSkeleton />,
  [WidgetType.BAR_V]: <VerticalBarSkeleton />,
  [WidgetType.BAR_H]: <HorizontalBarSkeleton />,
  [WidgetType.LINE]: <LineSkeleton />,
  [WidgetType.BIG_NUMBER]: <BigNumberSkeleton />
};

function ChartLoadingWrapper({
  isLoading,
  chartType,
  children
}: Readonly<ChartLoadingWrapperProps>) {
  if (!isLoading) {
    return <>{children}</>;
  }

  return (
    <div className="animate-pulse px-2" data-testid="chart-loading-shimmer">
      {SKELETON_BY_CHART_TYPE[chartType]}
    </div>
  );
}

export default ChartLoadingWrapper;
